import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Film, ImageIcon, RotateCcw, Wifi, WifiOff, X } from "lucide-react";
import { toast } from "sonner";
import { PageSkeleton } from "../../components/brand/Experience";
import { StepAudio, findExercise, setExerciseMode, useExerciseManifest, useNetwork } from "../../lib/exercises";
import { ModeSwitch } from "./ExerciseCatalog";

const Illustration = ({ step }) => (
  <img decoding="async" data-testid="step-illustration" src={step.svg} alt={step.title_fr} draggable={false}
    onError={(e) => { if (step.png && !e.currentTarget.src.endsWith(step.png)) e.currentTarget.src = step.png; }}
    className="h-full max-h-full w-full max-w-[520px] select-none rounded-[2rem] object-contain" />
);

const Media = ({ step, wantVideo, setWantVideo, wifiOk }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [step.video]);
  const showVideo = wantVideo && step.video && wifiOk && !failed;
  if (!showVideo) return <Illustration step={step} />;
  return (
    <video data-testid="step-video" key={step.video} src={step.video} poster={step.png} controls playsInline autoPlay muted
      className="h-full max-h-full w-full max-w-[520px] rounded-[2rem] bg-black object-contain"
      onError={() => { setFailed(true); setWantVideo(false); toast("Vidéo indisponible — illustration hors-ligne affichée"); }} />
  );
};

export default function ExercisePlayer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: m } = useExerciseManifest();
  const { online, wifiOk } = useNetwork();
  const [i, setI] = useState(0);
  const [done, setDone] = useState(false);
  const [wantVideo, setWantVideo] = useState(false);
  const touch = useRef(null);
  const back = () => (window.history.length > 1 ? nav(-1) : nav("/exercises"));
  const mode = params.get("mode") === "aidant" ? "aidant" : "patient";
  const found = findExercise(m, id);
  const steps = found?.exercise.steps || [];
  const step = steps[i];

  const go = (d) => {
    if (d > 0 && i === steps.length - 1) return setDone(true);
    setI((x) => Math.min(steps.length - 1, Math.max(0, x + d)));
  };
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); if (e.key === "Escape") back(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  if (!m) return <div className="p-6"><PageSkeleton /></div>;
  if (!found) return <div className="p-6" data-testid="exercise-not-found">Exercice introuvable.</div>;
  const setMode = (k) => { setExerciseMode(k); setParams({ mode: k }, { replace: true }); };
  const text = mode === "aidant" ? step.caregiver_fr : step.instruction_fr;
  const audioSrc = mode === "aidant" ? step.audio_caregiver : step.audio;

  return (
    <div className="bg-navy-depth fixed inset-0 z-40 flex flex-col text-white" data-testid="exercise-player"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 70) go(dx < 0 ? 1 : -1); }}>
      <header className="safe-top flex items-center justify-between gap-2 px-4 pb-2">
        <button data-testid="player-close" onClick={() => back()} aria-label="Fermer l'exercice" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10"><X /></button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-[11px] font-bold tracking-[0.2em] text-[#4FD1C5]">{found.pathology.name_fr.toUpperCase()}</p>
          <p className="truncate font-display text-lg font-semibold" data-testid="player-exercise-name">{found.exercise.name_fr}</p>
        </div>
        <span data-testid="network-status" aria-label={online ? (wifiOk ? "En ligne" : "Réseau limité") : "Hors-ligne"}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10">{online ? <Wifi className={`h-5 w-5 ${wifiOk ? "text-[#4FD1C5]" : "text-[#E0B33A]"}`} /> : <WifiOff className="h-5 w-5 text-[#E0B33A]" />}</span>
      </header>
      <div className="flex justify-center px-4 pb-2"><ModeSwitch mode={mode} onChange={setMode} dark /></div>

      {done ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center" data-testid="exercise-done">
          <CheckCircle2 className="h-20 w-20 text-[#4FD1C5]" />
          <h1 className="font-display text-4xl font-semibold">Exercice terminé</h1>
          <p className="max-w-sm text-white/75">Bravo pour votre régularité. Reposez-vous et signalez toute douleur à votre professionnel de santé.</p>
          <button data-testid="exercise-restart" onClick={() => { setI(0); setDone(false); }} className="flex h-14 items-center gap-2 rounded-full bg-white/10 px-6 font-bold"><RotateCcw className="h-5 w-5" />Recommencer</button>
          <button data-testid="exercise-back-catalog" onClick={() => back()} className="bg-brand h-14 rounded-full px-8 font-extrabold">Retour au catalogue</button>
        </div>
      ) : (
        <>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4">
            <AnimatePresence mode="wait">
              <motion.div key={`${i}-${wantVideo}`} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}
                className="flex h-full w-full items-center justify-center">
                <Media step={step} wantVideo={wantVideo} setWantVideo={setWantVideo} wifiOk={wifiOk} />
              </motion.div>
            </AnimatePresence>
            {step.video && (
              <button data-testid="video-toggle" onClick={() => (wifiOk ? setWantVideo((v) => !v) : toast("Vidéo disponible en Wi-Fi uniquement — illustration hors-ligne affichée"))}
                className="absolute right-6 top-3 flex h-11 items-center gap-2 rounded-full bg-black/40 px-4 text-xs font-bold backdrop-blur" aria-pressed={wantVideo}>
                {wantVideo ? <ImageIcon className="h-4 w-4" /> : <Film className="h-4 w-4" />}{wantVideo ? "Illustration" : "Vidéo (Wi-Fi)"}
              </button>
            )}
          </div>
          <section className="safe-bottom rounded-t-[2rem] bg-white px-5 pt-5 text-[#0A2540] dark:bg-[#0D2A45] dark:text-white" aria-live="polite">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold tracking-[0.2em] text-[#0F7C7C] dark:text-[#4FD1C5]" data-testid="step-counter">ÉTAPE {i + 1} / {steps.length}</p>
              {mode === "aidant" && <span data-testid="caregiver-badge" className="rounded-full bg-[#E0B33A] px-3 py-1 text-[10px] font-extrabold tracking-widest text-[#0A2540]">MODE AIDANT</span>}
            </div>
            <h2 data-testid="step-title" className="mt-1 font-display text-2xl font-semibold leading-tight">{step.title_fr}</h2>
            <p data-testid="step-instruction" className="mt-2 text-lg leading-snug opacity-90">{text}</p>
            <div className="mt-3 flex gap-1.5" aria-hidden>{steps.map((_, k) => <span key={k} className={`h-1.5 flex-1 rounded-full ${k <= i ? "bg-[#17A2A2]" : "bg-[#0A2540]/15 dark:bg-white/15"}`} />)}</div>
            <div className="mt-4 flex items-center gap-2 pb-4">
              <button data-testid="step-prev" onClick={() => go(-1)} disabled={i === 0} aria-label="Étape précédente"
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-[#0A2540]/15 disabled:opacity-30 dark:border-white/20"><ArrowLeft /></button>
              <StepAudio src={audioSrc} text={`${step.title_fr}. ${text}`} stepKey={`${id}-${i}-${mode}`} />
              <button data-testid="step-next" onClick={() => go(1)}
                className="bg-brand flex h-14 flex-1 items-center justify-center gap-2 rounded-full text-base font-extrabold text-white">
                {i === steps.length - 1 ? "Terminer" : "Suivant"}<ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
